// @flow

// Skipping story doc for this component since it is a base class component meant only to be extended by its subclasses
/* eslint-disable ta-code-conventions/has-matching-storydoc */

import { loadMessages } from '@ta/common.i18n';
import { loadFeatures } from '@ta/common.features';
import { ApiCaller } from '@ta/common.api';

/**
 * All of these fields will be filled in for you by babel-plugin-named-import-chunks
 * You just write an arrow function that calls import('my-component')
 */
type Importer = {|
  (): any,
  __MODULE_NAME__: string,
  __PACKAGE__: string,
  __MODULE_ID__: string,
  __IS_LOADED__: () => boolean,
  __GET_SYNC__: () => any,
  __BUNDLE_NAME__: string,
|};

const getBatchedResponse = (response, stagerId) => (Object.values(response[stagerId])[0]: any).body;

/**
 * An object that can load a module. Tracks the loading state of the
 * module and, in Node, records that it was loaded.
 *
 * Warning: if this is instantiated as a module variable, that module will be stateful!
 */
class Loadable {
  constructor(importer: () => any) {
    // This cast is safe, assuming the function actually imports something
    // and the Babel plugin ran.
    this.importer = ((importer: any): Importer);
  }

  importer: Importer;
  dispatched = false;
  recorded = false;
  loadingPromise = null;
  error = null;

  getName() {
    return this.importer.__MODULE_NAME__;
  }

  getPackage() {
    return this.importer.__PACKAGE__;
  }

  isLoaded() {
    return this.importer.__IS_LOADED__();
  }

  isLoading() {
    return this.loadingPromise != null;
  }

  isErrored() {
    return this.error != null;
  }

  // For this function code coverage was disabled since it's not possible it
  /* istanbul ignore next */
  stage(state: any, stage: any) {
    if (!this.isLoaded()) {
      // Can't stage if it isn't loaded
      return;
    }

    // Stage translations and features. If they're already available, also
    // dispatch them. This isn't ideal, but we don't want to dispatch in the
    // render method, and there doesn't seem to be a better approach.
    const { translations, features } = this.getHandles(stage);
    if (!this.dispatched && (translations.get().data || features.get().data)) {
      this.dispatched = true;
      loadMessages(translations.get().data);
      loadFeatures(features.get().data);
    }

    // If the module we're loading is available, check if we can stage it
    const module = this.get();
    if (typeof module.stage === 'function') {
      module.stage(state, stage);
    }
  }

  getHandles(stage: any) {
    const bundleName = (this.importer.__BUNDLE_NAME__ || '').replace(/\//g, '-');
    const translations = stage(`/data/1.0/translations/bundle?name=${bundleName}`);
    const features = stage(`/data/1.0/features/bundle?name=${bundleName}`);
    return { translations, features };
  }

  // For this function code coverage was disabled since it's not possible to test SERVER loading
  /* istanbul ignore next */
  get() {
    if (!this.isLoaded()) {
      throw new Error(`Loadable can't synchronously provide an unloaded module: ${this.getName()}`);
    }

    // On the server, record that this component was loaded
    if (SERVER && this.getPackage() && !this.recorded) {
      this.recorded = true;
      if (!global.__LOADED_COMPONENTS__) {
        global.__LOADED_COMPONENTS__ = [];
      }
      global.__LOADED_COMPONENTS__.push(this.importer.__BUNDLE_NAME__);
    }

    // Get default export
    return this.importer.__GET_SYNC__().default;
  }

  getSsrQueries() {
    if (!this.isLoaded()) {
      throw new Error(`Loadable can't synchronously provide an unloaded module: ${this.getName()}`);
    }
    return this.importer.__GET_SYNC__().ssrQueries;
  }

  // For this function code coverage was disabled since it's not possible to test SERVER case and dispatching
  /* istanbul ignore next */
  getPromise() {
    if (SERVER) {
      throw new Error(
        `Shouldn't be attempting to get Loadable promise on server! Module ${this.getName()}`,
      );
    }

    if (this.isLoaded()) {
      return Promise.resolve(this.get());
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    const promises = [];

    promises.push(
      this.importer().then(component => {
        if (typeof component.default.load === 'function') {
          component.default.load();
        }
      }),
    );

    const api = new ApiCaller();
    const { translations, features } = this.getHandles(api.stager);
    promises.push(
      api.resolve().then(json => {
        this.dispatched = true;
        loadMessages(getBatchedResponse(json, translations));
        loadFeatures(getBatchedResponse(json, features));
      }),
    );

    this.loadingPromise = Promise.all(promises)
      .then(() => {
        this.loadingPromise = null;
        return this.get();
      })
      .catch(e => {
        this.loadingPromise = null;
        this.error = e;
      });

    return this.loadingPromise;
  }
}

export default Loadable;
